import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Shield,
  Users,
  Crown,
  TrendingUp,
  DollarSign,
  Activity,
  Bell,
  Heart,
  LogOut,
  Search,
  ChevronRight,
  Trash2,
  ToggleRight,
  RefreshCw,
  BarChart3,
  Calendar,
  Loader2,
  CheckCircle,
  XCircle,
  Ban,
  LogIn,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("adminUser");
    if (!stored) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setAdminUser(JSON.parse(stored));
    fetchAnalytics();
  }, [navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/admin/analytics/overview");
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminUser");
    localStorage.removeItem("admin_token");
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = "") => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await api.get(`/admin/users${params}`);
      setUsers(response.data.users);
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const togglePremium = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/premium`);
      toast.success("Premium status updated");
      fetchUsers(searchQuery);
      if (selectedUser?.user_id === userId) {
        fetchUserDetail(userId);
      }
    } catch (error) {
      toast.error("Failed to update premium status");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deleted successfully");
      setDeleteConfirm(null);
      setSelectedUser(null);
      fetchUsers(searchQuery);
      fetchAnalytics();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const toggleBan = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/ban`);
      toast.success(res.data.is_banned ? "User banned" : "User unbanned");
      fetchUsers(searchQuery);
      if (selectedUser?.user_id === userId) {
        fetchUserDetail(userId);
      }
    } catch (error) {
      toast.error("Failed to update ban status");
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/admin/logout");
    } catch (e) {}
    localStorage.removeItem("adminUser");
    localStorage.removeItem("admin_token");
    navigate("/admin/login", { replace: true });
  };

  const [seeding, setSeeding] = useState(false);
  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/admin/seed-data");
      toast.success(res.data.message);
      fetchAnalytics();
      if (activeTab === "users") fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed data");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (activeTab === "users") {
        fetchUsers(searchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color = "text-[#D4AF37]" }) => (
    <div className="glass-card p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full ${color.replace('text-', 'bg-')}/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <span className="text-white/70 font-body text-sm">{label}</span>
      </div>
      <p className="font-display text-3xl text-white">{value}</p>
      {subValue && <p className="text-white/50 text-sm font-body mt-1">{subValue}</p>}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#070B14]" />
            </div>
            <div>
              <span className="font-display text-xl text-white">Admin Panel</span>
              <p className="text-xs text-white/50 font-body">{adminUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSeedData}
              disabled={seeding}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="admin-seed-btn"
            >
              {seeding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {seeding ? "Seeding..." : "Seed Test Data"}
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-red-400 hover:bg-red-500/10"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "users", label: "Users", icon: Users },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]"
                  : "text-white/60 hover:text-white"
              } border border-transparent`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && analytics && (
          <div className="space-y-6 animate-fade-in-up">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={analytics.total_users} />
              <StatCard icon={Crown} label="Premium Users" value={analytics.premium_users} color="text-[#D4AF37]" />
              <StatCard icon={Activity} label="Active (7d)" value={analytics.active_users_7d} color="text-green-400" />
              <StatCard icon={TrendingUp} label="New Today" value={analytics.new_users_today} color="text-[#8B5CF6]" />
            </div>

            {/* Revenue & Engagement */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-6">
                <h3 className="font-display text-xl text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Revenue
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Paid Transactions</span>
                    <span className="text-white font-display text-xl">{analytics.paid_transactions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Est. Monthly Revenue</span>
                    <span className="text-green-400 font-display text-xl">${analytics.monthly_revenue_estimate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Conversion Rate</span>
                    <span className="text-[#D4AF37] font-display text-xl">{analytics.premium_conversion_rate}%</span>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <h3 className="font-display text-xl text-white mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" />
                  Engagement
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Total Reflections</span>
                    <span className="text-white font-display text-xl">{analytics.total_reflections}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Guidances Generated</span>
                    <span className="text-white font-display text-xl">{analytics.total_guidances}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 font-body">Push Subscribers</span>
                    <span className="text-[#8B5CF6] font-display text-xl">{analytics.push_subscribers}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-card p-6">
              <h3 className="font-display text-xl text-white mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button onClick={fetchAnalytics} className="btn-secondary">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Stats
                </Button>
                <Button onClick={() => setActiveTab("users")} className="btn-secondary">
                  <Users className="w-4 h-4 mr-2" />
                  Manage Users
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="grid lg:grid-cols-3 gap-6 animate-fade-in-up">
            {/* User List */}
            <div className="lg:col-span-2 glass-card p-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="custom-input pl-10"
                    data-testid="user-search"
                  />
                </div>
              </div>

              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {users.map((user) => (
                    <button
                      key={user.user_id}
                      onClick={() => fetchUserDetail(user.user_id)}
                      className={`w-full p-4 rounded-xl border transition-all text-left ${
                        selectedUser?.user_id === user.user_id
                          ? "border-[#D4AF37] bg-[#D4AF37]/10"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.picture || `https://ui-avatars.com/api/?name=${user.first_name || user.name}&background=1E293B&color=fff`}
                            alt=""
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <p className="text-white font-body font-medium">{user.first_name || user.name}</p>
                            <p className="text-white/50 text-sm font-body">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user.is_banned && (
                            <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-body">
                              Banned
                            </span>
                          )}
                          {user.is_premium && (
                            <span className="px-2 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-body">
                              Premium
                            </span>
                          )}
                          <ChevronRight className="w-4 h-4 text-white/40" />
                        </div>
                      </div>
                    </button>
                  ))}
                  {users.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                      <p className="text-white/50 font-body">No users found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* User Detail */}
            <div className="glass-card p-6">
              {selectedUser ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <img
                      src={selectedUser.picture || `https://ui-avatars.com/api/?name=${selectedUser.first_name || selectedUser.name}&background=D4AF37&color=070B14&size=128`}
                      alt=""
                      className="w-20 h-20 rounded-full mx-auto mb-3"
                    />
                    <h3 className="font-display text-xl text-white">{selectedUser.first_name || selectedUser.name}</h3>
                    <p className="text-white/50 text-sm font-body">{selectedUser.email}</p>
                    <div className="flex items-center gap-2 justify-center mt-2">
                      {selectedUser.is_premium && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-body">
                          <Crown className="w-4 h-4" />
                          Premium
                        </span>
                      )}
                      {selectedUser.is_banned && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-body">
                          <Ban className="w-4 h-4" />
                          Banned
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">User ID</span>
                      <span className="text-white font-body font-mono text-xs">{selectedUser.user_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">DOB</span>
                      <span className="text-white font-body">{selectedUser.date_of_birth || "Not set"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Focus Areas</span>
                      <span className="text-white font-body">{(selectedUser.focus_areas || []).join(", ") || "None"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Streak</span>
                      <span className="text-white font-body">{selectedUser.current_streak || 0} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Reflections</span>
                      <span className="text-white font-body">{selectedUser.reflection_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Guidances</span>
                      <span className="text-white font-body">{selectedUser.guidance_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Login Count</span>
                      <span className="text-white font-body flex items-center gap-1">
                        <LogIn className="w-3 h-3 text-[#8B5CF6]" />
                        {selectedUser.login_count || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50 font-body">Joined</span>
                      <span className="text-white font-body text-xs">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : "N/A"}</span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <Button
                      onClick={() => togglePremium(selectedUser.user_id)}
                      className={selectedUser.is_premium ? "btn-secondary w-full" : "btn-gold w-full"}
                      data-testid="toggle-premium-btn"
                    >
                      <ToggleRight className="w-4 h-4 mr-2" />
                      {selectedUser.is_premium ? "Remove Premium" : "Grant Premium"}
                    </Button>
                    <Button
                      onClick={() => toggleBan(selectedUser.user_id)}
                      className={`w-full ${selectedUser.is_banned ? "bg-green-600 hover:bg-green-700 text-white" : "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"}`}
                      data-testid="toggle-ban-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      {selectedUser.is_banned ? "Unban User" : "Ban User"}
                    </Button>
                    <Button
                      onClick={() => setDeleteConfirm(selectedUser)}
                      variant="ghost"
                      className="w-full text-red-400 hover:bg-red-500/10"
                      data-testid="delete-user-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete User
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 font-body">Select a user to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0A101D] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Delete User?</DialogTitle>
            <DialogDescription className="text-white/60 font-body">
              This will permanently delete {deleteConfirm?.email} and all their data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button onClick={() => setDeleteConfirm(null)} className="btn-secondary">
              Cancel
            </Button>
            <Button
              onClick={() => deleteUser(deleteConfirm?.user_id)}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
